import React, { useContext, useEffect, useState } from "react"
import algoliasearch from "algoliasearch/lite"
import { InstantSearch, SearchBox, Configure } from "react-instantsearch"

import Hit from "./Hit"
import { ChildContext } from "../Layout"
import { escKeyHandler, outsideClickHandler } from "../../utils/Modal"
import NoResultsBoundary from "./NoResultsBoundary"
import NoResults from "./NoResults"
import InfiniteHitsFilm from "./InfiniteHitsFilm"

const searchClient = algoliasearch(
  process.env.GATSBY_ALGOLIA_APP_ID,
  process.env.GATSBY_ALGOLIA_SEARCH_KEY,
)

function getSearchInputElement() {
  return document.querySelector(".ais-SearchBox-input")
}

function getInfiniteHitsListElement() {
  return document.querySelector(".ais-InfiniteHits-list")
}

// https://stackoverflow.com/questions/71826145/show-keyboard-on-input-focus-without-user-action
export function openIosKeyboard() {
  const input = document.createElement("input")
  input.setAttribute("type", "text")
  input.setAttribute("style", "position: fixed; top: -100px; left: -100px;")
  document.body.appendChild(input)
  input.focus()
  // it's safe to remove the fake input after a 10s timeout
  setTimeout(() => {
    document.body.removeChild(input)
  }, 10 * 1000)
}

let debounceTimer
function SearchBoxFilm() {
  const { showSearchBox, setShowSearchBox, sessionStorageCache } =
    useContext(ChildContext)
  const [hasShown, setHasShown] = useState(false)
  const searchBoxId = "searchBox"

  const closeSearchBox = () => {
    setShowSearchBox(false)
  }

  const handleStateChange = ({ uiState, setUiState }) => {
    if (debounceTimer) clearTimeout(debounceTimer)

    debounceTimer = setTimeout(() => {
      getInfiniteHitsListElement()?.scrollTo(0, 0)
      setUiState(uiState)
    }, 500)
  }

  useEffect(() => {
    if (showSearchBox && !hasShown) {
      openIosKeyboard()
      setHasShown(true)
    }

    if (showSearchBox && hasShown) {
      getSearchInputElement()?.focus()
    }
  }, [showSearchBox, hasShown])

  // close the modal on click outside
  useEffect(() => {
    if (!showSearchBox) return

    const handler = outsideClickHandler(
      searchBoxId,
      showSearchBox,
      closeSearchBox,
    )

    const timer = setTimeout(() => {
      document.addEventListener("click", handler)
    }, 0)

    return () => {
      clearTimeout(timer)
      document.removeEventListener("click", handler)
    }
  }, [searchBoxId, showSearchBox, closeSearchBox])

  // close the modal if the esc key is pressed
  useEffect(() => {
    const handler = escKeyHandler(closeSearchBox)
    document.addEventListener("keydown", handler)

    return () => {
      document.removeEventListener("keydown", handler)
    }
  }, [closeSearchBox])

  return (
    <div
      className={`w-full max-w-3xl mx-auto ${
        showSearchBox ? "block" : "hidden"
      }`}
      id={searchBoxId}
    >
      <div className="fixed mt-12 sm:mt-14 z-10 mx-auto max-w-3xl w-full px-4 sm:px-6 shadow-md">
        <div className="bg-gray-800 rounded-b-lg shadow-2xl shadow-black/50 border border-t-0 border-gray-700 overflow-hidden">
          <InstantSearch
            searchClient={searchClient}
            indexName={process.env.GATSBY_ALGOLIA_INDEX_NAME}
            onStateChange={({ uiState, setUiState }) => {
              handleStateChange({ uiState, setUiState })
            }}
          >
            {hasShown && (
              <>
                <Configure attributesToRetrieve={["*"]} />
                <SearchBox
                  autoFocus={true}
                  placeholder="Search for a blog post..."
                  classNames={{
                    root: "border-b border-gray-700",
                    form: "bg-gray-800 relative py-3 px-4 w-full h-full",
                    input:
                      "rounded-md w-full py-2.5 pl-4 pr-10 bg-gray-700 text-gray-200 placeholder-gray-400 border border-gray-600 focus:outline-none focus:border-fBlue-800 focus:ring-fBlue-800 focus:ring-1 appearance-none",
                    submit: "hidden",
                    reset: "absolute right-7 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200",
                    loadingIndicator: "absolute right-7 top-1/2 -translate-y-1/2",
                  }}
                />

                <NoResultsBoundary fallback={<NoResults />}>
                  <InfiniteHitsFilm
                    hitComponent={Hit}
                    cache={sessionStorageCache}
                  />
                </NoResultsBoundary>
              </>
            )}
          </InstantSearch>
        </div>
      </div>
    </div>
  )
}

export default SearchBoxFilm
